import {
  ChartBarIcon,
  CheckCircleIcon,
  CodeBracketIcon,
  RocketLaunchIcon,
  SparklesIcon,
  StarIcon,
  TicketIcon,
} from '@heroicons/react/24/outline';
import * as Tabs from '@radix-ui/react-tabs';
import { motion } from 'framer-motion';
import {
  BarChart3,
  Headphones,
  Percent,
  PieChart,
  Table,
  Target,
  Timer,
  Trophy,
  Zap,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import { SUPPORT_METRICS, TECH_TEAM_METRICS } from '../../constants/metrics.constants';
import Header from '../layout/Header';
import AnalyticsLoading from './components/AnalyticsLoading';
import CaseOriginDonutChart from './components/CaseOriginDonutChart';
import CaseProductDonutChart from './components/CaseProductDonutChart';
import CaseTypeBarChart from './components/CaseTypeBarChart';
import CaseTypeByRepChart from './components/CaseTypeByRepChart';
import CategoryDonutChart from './components/CategoryDonutChart';
import DashboardNavigation from './components/DashboardNavigation';
import MetricCard from './components/MetricCard';
import ModernDatePicker from './components/ModernDatePicker';
import TimeDistributionChart from './components/TimeDistributionChart';
import { TimeDistributionData } from './components/timeDistributionUtils';
import analyticsService, { DateFilter } from './services/analytics.service';

// Beautiful color system inspired by sales dashboard
const SUPPORT_COLORS = [
  {
    background: 'bg-gradient-to-r from-blue-50/60 via-blue-25/30 to-blue-50/60',
    border: 'border-l-blue-400',
    shadow: 'shadow-blue-100/50',
  },
  {
    background: 'bg-gradient-to-r from-emerald-50/60 via-emerald-25/30 to-emerald-50/60',
    border: 'border-l-emerald-400',
    shadow: 'shadow-emerald-100/50',
  },
  {
    background: 'bg-gradient-to-r from-purple-50/60 via-purple-25/30 to-purple-50/60',
    border: 'border-l-purple-400',
    shadow: 'shadow-purple-100/50',
  },
  {
    background: 'bg-gradient-to-r from-orange-50/60 via-orange-25/30 to-orange-50/60',
    border: 'border-l-orange-400',
    shadow: 'shadow-orange-100/50',
  },
  {
    background: 'bg-gradient-to-r from-cyan-50/60 via-cyan-25/30 to-cyan-50/60',
    border: 'border-l-cyan-400',
    shadow: 'shadow-cyan-100/50',
  },
  {
    background: 'bg-gradient-to-r from-pink-50/60 via-pink-25/30 to-pink-50/60',
    border: 'border-l-pink-400',
    shadow: 'shadow-pink-100/50',
  },
  {
    background: 'bg-gradient-to-r from-indigo-50/60 via-indigo-25/30 to-indigo-50/60',
    border: 'border-l-indigo-400',
    shadow: 'shadow-indigo-100/50',
  },
  {
    background: 'bg-gradient-to-r from-teal-50/60 via-teal-25/30 to-teal-50/60',
    border: 'border-l-teal-400',
    shadow: 'shadow-teal-100/50',
  },
];

// Helper function to get color theme for each agent row
const getAgentColorTheme = (index: number) => {
  return SUPPORT_COLORS[index % SUPPORT_COLORS.length];
};

const SupportDashboard = () => {
  const [dateFilter, setDateFilter] = useState<DateFilter>({
    type: 'monthToDate',
  });
  const [minimumDurationComplete, setMinimumDurationComplete] = useState(false);
  const [activeTab, setActiveTab] = useState('metrics');
  const [hoveredRowIndex, setHoveredRowIndex] = useState<number | null>(null);

  const { data, isLoading, error, refetch, isFetching } = useQuery(
    ['analytics-metrics', 'support', dateFilter],
    () => analyticsService.getMetrics('support', dateFilter),
    {
      refetchOnWindowFocus: false,
      staleTime: 2 * 60 * 1000, // 2 minutes
      keepPreviousData: true,
    }
  );

  // Prepare metrics data before any conditional returns to follow Rules of Hooks
  const allMetrics = data?.metrics || [];

  // Tech Team metric IDs set for filtering
  const techTeamMetricIds = new Set<string>(Object.values(TECH_TEAM_METRICS));

  // Filter metrics for Metrics tab (exclude visualization-only, leaderboard-only, and tech team metrics)
  const metricsTabMetrics = useMemo(() => {
    return allMetrics.filter(
      metric =>
        metric.id !== SUPPORT_METRICS.CASE_ORIGIN_BREAKDOWN &&
        metric.id !== SUPPORT_METRICS.CASE_PRODUCT_BREAKDOWN &&
        metric.id !== SUPPORT_METRICS.CASE_TYPE_BREAKDOWN &&
        metric.id !== SUPPORT_METRICS.CASE_TYPE_BY_REP_BREAKDOWN &&
        metric.id !== SUPPORT_METRICS.FEATURE_AREA_CLASSIFICATION_BREAKDOWN &&
        metric.id !== SUPPORT_METRICS.SENTIMENT_CLASSIFICATION_BREAKDOWN &&
        metric.id !== SUPPORT_METRICS.FIRST_RESPONSE_TIME_DISTRIBUTION &&
        metric.id !== SUPPORT_METRICS.RESOLUTION_TIME_DISTRIBUTION &&
        metric.id !== SUPPORT_METRICS.TOTAL_TICKETS_LEADERBOARD &&
        metric.id !== SUPPORT_METRICS.ALL_TIME_TICKETS_LEADERBOARD &&
        metric.id !== SUPPORT_METRICS.AVG_FIRST_RESPONSE_TIME_LEADERBOARD &&
        metric.id !== SUPPORT_METRICS.AVG_RESOLUTION_TIME_LEADERBOARD &&
        metric.id !== SUPPORT_METRICS.CASES_ESCALATED_DEV_LEADERBOARD &&
        metric.id !== SUPPORT_METRICS.DEV_ESCALATION_RATE_LEADERBOARD &&
        metric.id !== SUPPORT_METRICS.FIRST_TOUCH_RESOLUTION_LEADERBOARD &&
        metric.id !== SUPPORT_METRICS.FIRST_TOUCH_RESOLUTION_RATE_LEADERBOARD &&
        metric.id !== SUPPORT_METRICS.CSAT_SUBMISSIONS &&
        metric.id !== SUPPORT_METRICS.CSAT_AVG_SCORE &&
        metric.id !== SUPPORT_METRICS.CSAT_COMMENTS &&
        metric.id !== SUPPORT_METRICS.CSAT_DETAILS &&
        !techTeamMetricIds.has(metric.id)
    );
  }, [allMetrics]);
  const techTeamMetrics = useMemo(() => {
    return allMetrics.filter(metric => techTeamMetricIds.has(metric.id));
  }, [allMetrics]);

  // Convert metrics array to object for direct access instead of O(n) find operations
  const metricsMap = useMemo(() => {
    const map: Record<string, number> = {};
    allMetrics.forEach(metric => {
      if (typeof metric?.value === 'number') {
        map[metric.id] = metric.value;
      }
    });

    return map;
  }, [allMetrics]);

  // Optimized metric value getter - O(1) lookup instead of O(n) find
  const getMetricValue = (id: string): number => {
    return metricsMap[id] ?? 0;
  };

  // CSAT details records for table view
  const csatDetailsRecords = useMemo(() => {
    const metric = allMetrics.find(m => m.id === SUPPORT_METRICS.CSAT_DETAILS);

    if (metric && Array.isArray(metric.value)) {
      return metric.value as Array<{
        Id?: string;
        CaseNumber?: string;
        Account_Name__c?: string;
        Owner?: { Name?: string };
        CSAT__c?: number;
        CSAT_Comment__c?: string;
        CSAT_Submitted_Date__c?: string;
      }>;
    }

    return [];
  }, [allMetrics]);

  const [csatPage, setCsatPage] = useState(0);
  const CSAT_PAGE_SIZE = 20;
  const [expandedCaseRows, setExpandedCaseRows] = useState<Record<string, boolean>>({});
  const [expandedAccountRows, setExpandedAccountRows] = useState<Record<string, boolean>>({});
  const [expandedOwnerRows, setExpandedOwnerRows] = useState<Record<string, boolean>>({});
  const [expandedCommentRows, setExpandedCommentRows] = useState<Record<string, boolean>>({});
  const [selectedOwners, setSelectedOwners] = useState<string[]>(['all']);
  const [isOwnerDropdownOpen, setIsOwnerDropdownOpen] = useState(false);
  const [dateSortOrder, setDateSortOrder] = useState<'desc' | 'asc'>('desc');
  const [isDateSortDropdownOpen, setIsDateSortDropdownOpen] = useState(false);

  useEffect(() => {
    setCsatPage(0);
    setExpandedAccountRows({});
    setExpandedCommentRows({});
    setSelectedOwners(['all']);
    setIsOwnerDropdownOpen(false);
  }, [dateFilter]);

  // Owners dropdown options derived from CSAT detail records
  const csatOwners = useMemo(() => {
    const owners = new Set<string>();
    csatDetailsRecords.forEach(r => {
      if (r.Owner?.Name) owners.add(r.Owner.Name);
    });

    return Array.from(owners).sort();
  }, [csatDetailsRecords]);

  // Filtered and sorted records by selected owner(s) and date sort order
  const filteredCsatRecords = useMemo(() => {
    let filtered = csatDetailsRecords;

    // Filter by owner
    if (!selectedOwners.includes('all') && selectedOwners.length > 0) {
      filtered = filtered.filter(r => r.Owner?.Name && selectedOwners.includes(r.Owner.Name));
    }

    // Sort by submitted date
    const sorted = [...filtered].sort((a, b) => {
      const dateA = a.CSAT_Submitted_Date__c ? new Date(a.CSAT_Submitted_Date__c).getTime() : 0;
      const dateB = b.CSAT_Submitted_Date__c ? new Date(b.CSAT_Submitted_Date__c).getTime() : 0;

      return dateSortOrder === 'desc' ? dateB - dateA : dateA - dateB;
    });

    return sorted;
  }, [selectedOwners, csatDetailsRecords, dateSortOrder]);

  // Derived metrics when owner filter is applied
  const derivedCsatMetrics = useMemo(() => {
    const submissions = filteredCsatRecords.length;
    const scored = filteredCsatRecords.filter(
      r =>
        typeof r.CSAT__c === 'number' &&
        Number.isFinite(r.CSAT__c) &&
        r.CSAT__c >= 1 &&
        r.CSAT__c <= 5
    );
    const avg =
      scored.length > 0 ? scored.reduce((sum, r) => sum + (r.CSAT__c || 0), 0) / scored.length : 0;
    const comments = filteredCsatRecords.filter(r => {
      const c = typeof r.CSAT_Comment__c === 'string' ? r.CSAT_Comment__c.trim() : '';

      return c.length > 0;
    }).length;

    return {
      submissions,
      avgScore: avg,
      comments,
    };
  }, [filteredCsatRecords]);

  // Show full loading screen for initial load OR filter changes
  const shouldShowLoading = isLoading || isFetching || !minimumDurationComplete;

  if (shouldShowLoading) {
    return <AnalyticsLoading onMinimumDurationComplete={() => setMinimumDurationComplete(true)} />;
  }

  const dateOptions = [
    { value: 'today', label: 'Today' },
    { value: 'past7days', label: 'Past 7 Days' },
    { value: 'monthToDate', label: 'Month to Date' },
    { value: 'past90days', label: 'Past 90 Days' },
    { value: 'yearToDate', label: 'Year to Date' },
    { value: 'custom', label: 'Custom Range' },
  ] as const;

  // Handle date filter change
  const handleFilterChange = (type: DateFilter['type']) => {
    if (type === 'custom') {
      setDateFilter({
        type,
        startDate: dateFilter.startDate || new Date().toISOString().split('T')[0],
        endDate: dateFilter.endDate || new Date().toISOString().split('T')[0],
      });
    } else {
      setDateFilter({ type });
    }
  };

  // Handle custom date change
  const handleDateChange = (field: 'startDate' | 'endDate', date: Date | null) => {
    if (date) {
      setDateFilter({
        ...dateFilter,
        [field]: date.toISOString().split('T')[0],
      });
    }
  };

  // Convert date strings to Date objects for DatePicker
  const getDateValue = (dateString?: string): Date | undefined => {
    return dateString ? new Date(dateString + 'T00:00:00') : undefined;
  };

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <Header />
        <div className="p-6">
          <div className="max-w-md mx-auto mt-20 text-center">
            <div className="text-red-500 text-6xl mb-4">⚠️</div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
              Unable to load support metrics
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              There was an error loading the support dashboard data.
            </p>
            <button
              onClick={() => refetch()}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 relative">
      <Header />
      <DashboardNavigation currentDashboardId="support" />

      <div className="p-6">
        {/* Enhanced Filter Section - Sales Dashboard Style */}
        <div className="mb-8">
          <motion.div
            className="bg-gradient-to-r from-emerald-600 via-emerald-700 to-green-700 rounded-2xl p-6 shadow-xl"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
              {/* Title Section */}
              <motion.div
                className="flex flex-col gap-4"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2, duration: 0.6 }}
              >
                {/* Title and Icon Row */}
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-white/20 rounded-lg backdrop-blur-sm">
                    <Headphones className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-white">Support Performance Metrics</h2>
                    <p className="text-emerald-100 text-sm">
                      Track your support team's performance
                    </p>
                  </div>
                </div>

                {/* Live Data Indicator */}
                <motion.div
                  className="flex items-center gap-2 px-3 py-1.5 bg-white/10 backdrop-blur-sm rounded-lg border border-white/20 self-start ml-14"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.8, duration: 0.5 }}
                >
                  {/* Animated Pulse Dot */}
                  <div className="relative flex items-center">
                    <motion.div
                      className="w-2 h-2 bg-emerald-400 rounded-full"
                      animate={{
                        scale: [1, 1.2, 1],
                        opacity: [0.8, 1, 0.8],
                      }}
                      transition={{
                        duration: 2,
                        repeat: Infinity,
                        ease: 'easeInOut',
                      }}
                    />
                    {/* Ripple Effect */}
                    <motion.div
                      className="absolute inset-0 bg-emerald-400 rounded-full"
                      animate={{
                        scale: [1, 2.5],
                        opacity: [0.6, 0],
                      }}
                      transition={{
                        duration: 2,
                        repeat: Infinity,
                        ease: 'easeInOut',
                      }}
                    />
                  </div>
                  <span className="text-white/90 text-xs font-medium tracking-wide">LIVE DATA</span>
                  <span className="text-white/70 text-xs">
                    • Updated{' '}
                    {new Date().toLocaleTimeString('en-US', {
                      hour: 'numeric',
                      minute: '2-digit',
                      hour12: true,
                    })}
                  </span>
                </motion.div>
              </motion.div>

              {/* Date Filter Controls */}
              <motion.div
                className="flex flex-wrap items-center gap-2"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.4, duration: 0.6 }}
              >
                {dateOptions.map(option => (
                  <button
                    key={option.value}
                    onClick={() => handleFilterChange(option.value)}
                    className={`px-4 py-2 rounded-xl text-sm font-medium transition-all duration-200 border-2 ${
                      dateFilter.type === option.value
                        ? 'bg-white text-emerald-700 border-white shadow-lg'
                        : 'text-white/90 border-white/30 hover:border-white/50 hover:bg-white/10'
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </motion.div>
            </div>

            {/* Custom Date Pickers */}
            {dateFilter.type === 'custom' && (
              <motion.div
                className="mt-4 pt-4 border-t border-white/20"
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.3, ease: 'easeInOut' }}
              >
                <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-white/90 font-medium">Start Date</span>
                    <ModernDatePicker
                      selected={getDateValue(dateFilter.startDate)}
                      onChange={date => handleDateChange('startDate', date)}
                      placeholder="MM/DD/YYYY"
                      maxDate={new Date()}
                      align="start"
                      calendarType="start-date"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-white/90 font-medium">End Date</span>
                    <ModernDatePicker
                      selected={getDateValue(dateFilter.endDate)}
                      onChange={date => handleDateChange('endDate', date)}
                      placeholder="MM/DD/YYYY"
                      minDate={getDateValue(dateFilter.startDate) || undefined}
                      maxDate={new Date()}
                      align="start"
                      calendarType="end-date"
                    />
                  </div>
                </div>
              </motion.div>
            )}
          </motion.div>
        </div>

        {/* Tab Navigation - Same style as Sales Dashboard */}
        <Tabs.Root value={activeTab} onValueChange={setActiveTab}>
          <Tabs.List className="grid w-full grid-cols-1 sm:grid-cols-6 gap-2.5 p-2 bg-gradient-to-r from-slate-50/50 to-gray-50/80 backdrop-blur-sm rounded-2xl border border-white/20 shadow-2xl max-w-5xl mx-auto mb-8">
            <Tabs.Trigger
              value="metrics"
              className="group relative flex items-center justify-center px-4 py-3 rounded-xl font-semibold text-sm transition-all duration-500 ease-in-out transform data-[state=active]:bg-gradient-to-br data-[state=active]:from-purple-500 data-[state=active]:via-indigo-500 data-[state=active]:to-blue-600 data-[state=active]:text-white data-[state=active]:shadow-lg data-[state=active]:scale-[1.02] data-[state=inactive]:bg-white/60 data-[state=inactive]:text-gray-600 data-[state=inactive]:hover:bg-white/80 data-[state=inactive]:hover:scale-102 data-[state=inactive]:hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-purple-400 focus:ring-offset-2 focus:ring-offset-transparent"
            >
              <motion.div
                className="flex items-center space-x-2"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <Table className="h-4 w-4" />
                <span>Metrics</span>
              </motion.div>
            </Tabs.Trigger>
            <Tabs.Trigger
              value="tech-team"
              className="group relative flex items-center justify-center px-4 py-3 rounded-xl font-semibold text-sm transition-all duration-500 ease-in-out transform data-[state=active]:bg-gradient-to-br data-[state=active]:from-rose-500 data-[state=active]:via-red-500 data-[state=active]:to-orange-600 data-[state=active]:text-white data-[state=active]:shadow-lg data-[state=active]:scale-[1.02] data-[state=inactive]:bg-white/60 data-[state=inactive]:text-gray-600 data-[state=inactive]:hover:bg-white/80 data-[state=inactive]:hover:scale-102 data-[state=inactive]:hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-rose-400 focus:ring-offset-2 focus:ring-offset-transparent"
            >
              <motion.div
                className="flex items-center space-x-2"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <CodeBracketIcon className="h-4 w-4" />
                <span>Tech Team</span>
              </motion.div>
            </Tabs.Trigger>
            <Tabs.Trigger
              value="visualizations"
              className="group relative flex items-center justify-center px-4 py-3 rounded-xl font-semibold text-sm transition-all duration-500 ease-in-out transform data-[state=active]:bg-gradient-to-br data-[state=active]:from-emerald-500 data-[state=active]:via-green-500 data-[state=active]:to-teal-600 data-[state=active]:text-white data-[state=active]:shadow-lg data-[state=active]:scale-[1.02] data-[state=inactive]:bg-white/60 data-[state=inactive]:text-gray-600 data-[state=inactive]:hover:bg-white/80 data-[state=inactive]:hover:scale-102 data-[state=inactive]:hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:ring-offset-2 focus:ring-offset-transparent"
            >
              <motion.div
                className="flex items-center space-x-2"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <PieChart className="h-4 w-4" />
                <span>Visualizations</span>
              </motion.div>
            </Tabs.Trigger>
            <Tabs.Trigger
              value="leaderboard"
              className="group relative flex items-center justify-center px-4 py-3 rounded-xl font-semibold text-sm transition-all duration-500 ease-in-out transform data-[state=active]:bg-gradient-to-br data-[state=active]:from-amber-500 data-[state=active]:via-orange-500 data-[state=active]:to-red-600 data-[state=active]:text-white data-[state=active]:shadow-lg data-[state=active]:scale-[1.02] data-[state=inactive]:bg-white/60 data-[state=inactive]:text-gray-600 data-[state=inactive]:hover:bg-white/80 data-[state=inactive]:hover:scale-102 data-[state=inactive]:hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-amber-400 focus:ring-offset-2 focus:ring-offset-transparent"
            >
              <motion.div
                className="flex items-center space-x-2"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <Trophy className="h-4 w-4" />
                <span>Leaderboard</span>
              </motion.div>
            </Tabs.Trigger>
            <Tabs.Trigger
              value="csat"
              className="group relative flex items-center justify-center px-4 py-3 rounded-xl font-semibold text-sm transition-all duration-500 ease-in-out transform data-[state=active]:bg-gradient-to-br data-[state=active]:from-cyan-500 data-[state=active]:via-sky-500 data-[state=active]:to-blue-600 data-[state=active]:text-white data-[state=active]:shadow-lg data-[state=active]:scale-[1.02] data-[state=inactive]:bg-white/60 data-[state=inactive]:text-gray-600 data-[state=inactive]:hover:bg-white/80 data-[state=inactive]:hover:scale-102 data-[state=inactive]:hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-cyan-400 focus:ring-offset-2 focus:ring-offset-transparent"
            >
              <motion.div
                className="flex items-center space-x-2"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <SparklesIcon className="h-4 w-4" />
                <span>CSAT Metrics</span>
              </motion.div>
            </Tabs.Trigger>
            <Tabs.Trigger
              value="classifications"
              className="group relative flex items-center justify-center px-4 py-3 rounded-xl font-semibold text-sm transition-all duration-500 ease-in-out transform data-[state=active]:bg-gradient-to-br data-[state=active]:from-fuchsia-500 data-[state=active]:via-purple-500 data-[state=active]:to-indigo-600 data-[state=active]:text-white data-[state=active]:shadow-lg data-[state=active]:scale-[1.02] data-[state=inactive]:bg-white/60 data-[state=inactive]:text-gray-600 data-[state=inactive]:hover:bg-white/80 data-[state=inactive]:hover:scale-102 data-[state=inactive]:hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-fuchsia-400 focus:ring-offset-2 focus:ring-offset-transparent"
            >
              <motion.div
                className="flex items-center space-x-2"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <Target className="h-4 w-4" />
                <span>Classifications</span>
              </motion.div>
            </Tabs.Trigger>
          </Tabs.List>

          {/* Tab Content */}
          <Tabs.Content value="metrics" className="focus:outline-none">
            {/* Enhanced Metrics Layout */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="space-y-12"
            >
              {/* Hero Metrics - Key Performance Indicators */}
              <motion.div
                className="relative bg-gradient-to-br from-white via-blue-50/30 to-purple-50/30 dark:from-gray-800/50 dark:via-blue-900/20 dark:to-purple-900/20 rounded-3xl p-8 shadow-xl border border-white/20 dark:border-gray-700/50 backdrop-blur-sm"
                initial={{ opacity: 0, y: 40, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ delay: 0.2, type: 'spring', stiffness: 80 }}
                whileHover={{ y: -2, boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)' }}
              >
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.3 }}
                  className="mb-8"
                >
                  <h3 className="text-2xl font-bold bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-600 bg-clip-text text-transparent mb-2 flex items-center gap-3">
                    <ChartBarIcon className="w-8 h-8 text-blue-600" />
                    Key Performance Indicators
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400 text-sm">
                    Primary metrics for support team performance tracking
                  </p>
                </motion.div>

                {/* 2x2 Grid Layout for KPIs - Consistent card sizes */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  {metricsTabMetrics
                    .filter(
                      metric =>
                        metric.id === 'support-total-tickets' ||
                        metric.id === 'support-total-tickets-all-time' ||
                        metric.id === 'support-essentials-cases' ||
                        metric.id === 'support-ehr-cases'
                    )
                    .map((metric, index) => (
                      <motion.div
                        key={metric.id}
                        initial={{ opacity: 0, y: 30, scale: 0.9 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        transition={{ delay: 0.4 + index * 0.1, type: 'spring', stiffness: 100 }}
                        className="transform hover:scale-105 transition-all duration-300"
                      >
                        <MetricCard metric={metric} />
                      </motion.div>
                    ))}
                </div>
              </motion.div>

              {/* Closing Metrics - Case Closure Analytics */}
              <motion.div
                className="relative bg-gradient-to-br from-white via-cyan-50/30 to-blue-50/30 dark:from-gray-800/50 dark:via-cyan-900/20 dark:to-blue-900/20 rounded-3xl p-8 shadow-xl border border-white/20 dark:border-gray-700/50 backdrop-blur-sm"
                initial={{ opacity: 0, y: 40, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ delay: 0.5, type: 'spring', stiffness: 80 }}
                whileHover={{ y: -2, boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)' }}
              >
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.6 }}
                  className="mb-8"
                >
                  <h3 className="text-2xl font-bold bg-gradient-to-r from-cyan-600 via-blue-600 to-indigo-600 bg-clip-text text-transparent mb-2 flex items-center gap-3">
                    <CheckCircleIcon className="w-8 h-8 text-cyan-600" />
                    Closing Metrics
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400 text-sm">
                    Case closure analytics and subscriber self-service metrics
                  </p>
                </motion.div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                  {metricsTabMetrics
                    .filter(
                      metric =>
                        metric.id === 'support-cases-closed-in-timeframe' ||
                        metric.id === 'support-cases-closed-by-subscribers' ||
                        metric.id === 'support-subscriber-closed-rate'
                    )
                    .map((metric, index) => (
                      <motion.div
                        key={metric.id}
                        initial={{ opacity: 0, y: 30, scale: 0.9 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        transition={{ delay: 0.7 + index * 0.1, type: 'spring', stiffness: 100 }}
                        className="transform hover:scale-105 transition-all duration-300"
                      >
                        <MetricCard metric={metric} />
                      </motion.div>
                    ))}
                </div>
              </motion.div>

              {/* Performance Metrics - Response & Resolution Times */}
              <motion.div
                className="relative bg-gradient-to-br from-white via-emerald-50/30 to-teal-50/30 dark:from-gray-800/50 dark:via-emerald-900/20 dark:to-teal-900/20 rounded-3xl p-8 shadow-xl border border-white/20 dark:border-gray-700/50 backdrop-blur-sm"
                initial={{ opacity: 0, y: 40, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ delay: 0.7, type: 'spring', stiffness: 80 }}
                whileHover={{ y: -2, boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)' }}
              >
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 1.0 }}
                  className="mb-8"
                >
                  <h3 className="text-2xl font-bold bg-gradient-to-r from-emerald-600 via-teal-600 to-cyan-600 bg-clip-text text-transparent mb-2 flex items-center gap-3">
                    <RocketLaunchIcon className="w-8 h-8 text-emerald-600" />
                    Response & Resolution Performance
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400 text-sm">
                    Time-based metrics for service quality assessment
                  </p>
                </motion.div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  {metricsTabMetrics
                    .filter(
                      metric =>
                        metric.id === 'support-avg-first-response-time' ||
                        metric.id === 'support-avg-first-response-time-bh' ||
                        metric.id === 'support-avg-resolution-time' ||
                        metric.id === 'support-avg-resolution-time-bh'
                    )
                    .map((metric, index) => (
                      <motion.div
                        key={metric.id}
                        initial={{ opacity: 0, y: 30, scale: 0.9 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        transition={{ delay: 1.1 + index * 0.1, type: 'spring', stiffness: 100 }}
                        className="transform hover:scale-105 transition-all duration-300"
                      >
                        <MetricCard metric={metric} />
                      </motion.div>
                    ))}
                </div>
              </motion.div>

              {/* Efficiency Metrics - First Touch & Escalations */}
              <motion.div
                className="relative bg-gradient-to-br from-white via-orange-50/30 to-pink-50/30 dark:from-gray-800/50 dark:via-orange-900/20 dark:to-pink-900/20 rounded-3xl p-8 shadow-xl border border-white/20 dark:border-gray-700/50 backdrop-blur-sm"
                initial={{ opacity: 0, y: 40, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ delay: 0.9, type: 'spring', stiffness: 80 }}
                whileHover={{ y: -2, boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)' }}
              >
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 1.3 }}
                  className="mb-8"
                >
                  <h3 className="text-2xl font-bold bg-gradient-to-r from-orange-600 via-red-600 to-pink-600 bg-clip-text text-transparent mb-2 flex items-center gap-3">
                    <SparklesIcon className="w-8 h-8 text-orange-600" />
                    Efficiency & Quality Metrics
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400 text-sm mb-3">
                    Advanced metrics for process optimization and quality tracking
                  </p>

                  {/* Important Note about Product Requests */}
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 1.2 }}
                    className="mt-4 p-5 bg-gradient-to-br from-cyan-50/80 via-blue-50/60 to-indigo-50/80 dark:from-cyan-900/10 dark:via-blue-900/10 dark:to-indigo-900/10 border border-cyan-200/50 dark:border-cyan-700/30 rounded-xl shadow-sm"
                  >
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0 p-2 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-lg shadow-md">
                        <RocketLaunchIcon className="w-5 h-5 text-white" />
                      </div>
                      <div className="flex-1">
                        <h4 className="text-sm font-bold text-gray-900 dark:text-white mb-2 flex items-center gap-2">
                          Product Request Tracking
                          <span className="text-xs px-2 py-0.5 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded-full font-semibold">
                            Enhanced Accuracy
                          </span>
                        </h4>
                        <div className="space-y-2 text-xs text-gray-700 dark:text-gray-300 leading-relaxed">
                          <p>
                            <strong className="text-cyan-600 dark:text-cyan-400">
                              Product Requests Submitted
                            </strong>{' '}
                            tracks actual submissions from Salesforce to Monday.com. Average time is
                            calculated from case creation to submission. For Alexis, it's measured
                            from last reassignment to ensure accurate workload reflection.
                          </p>
                          <div className="flex items-center gap-2 pt-1">
                            <div className="h-px flex-1 bg-gradient-to-r from-transparent via-cyan-200 dark:via-cyan-800 to-transparent"></div>
                            <span className="px-3 py-1 bg-gradient-to-r from-cyan-500/10 to-blue-500/10 border border-cyan-300/50 dark:border-cyan-700/50 rounded-full text-cyan-700 dark:text-cyan-300 font-semibold">
                              📅 Data available from Oct 14, 2024 onwards
                            </span>
                            <div className="h-px flex-1 bg-gradient-to-r from-transparent via-cyan-200 dark:via-cyan-800 to-transparent"></div>
                          </div>
                          <p className="text-gray-600 dark:text-gray-400 italic pt-1">
                            <strong>Note:</strong> Dev escalation metrics remain for historical
                            reference. Product Request metrics now provide the most current and
                            precise tracking.
                          </p>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                </motion.div>

                {/* Grid Layout for Efficiency Metrics - Grouped by related metrics */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  {/* Arrange metrics in desired order: Dev Escalation pair, Product Requests pair, First Touch pair */}
                  {[
                    'support-cases-escalated-dev',
                    'support-dev-escalation-rate',
                    'support-product-requests-submitted',
                    'support-avg-product-request-time',
                    'support-first-touch-resolution',
                    'support-first-touch-resolution-rate',
                  ]
                    .map(metricId => metricsTabMetrics.find(m => m.id === metricId))
                    .filter(Boolean)
                    .map((metric, index) => (
                      <motion.div
                        key={metric!.id}
                        initial={{ opacity: 0, y: 30, scale: 0.9 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        transition={{ delay: 1.4 + index * 0.1, type: 'spring', stiffness: 100 }}
                        className="transform hover:scale-105 transition-all duration-300"
                      >
                        <MetricCard metric={metric!} />
                      </motion.div>
                    ))}
                </div>
              </motion.div>

              {/* Decorative Elements */}
              <div className="absolute top-0 left-0 w-full h-full pointer-events-none overflow-hidden">
                <div className="absolute top-20 right-10 w-32 h-32 bg-gradient-to-br from-blue-400/10 to-purple-400/10 rounded-full blur-3xl"></div>
                <div className="absolute top-96 left-10 w-24 h-24 bg-gradient-to-br from-emerald-400/10 to-teal-400/10 rounded-full blur-2xl"></div>
                <div className="absolute bottom-20 right-20 w-28 h-28 bg-gradient-to-br from-orange-400/10 to-pink-400/10 rounded-full blur-3xl"></div>
              </div>
            </motion.div>

            {/* Empty State */}
            {metricsTabMetrics.length === 0 && !isLoading && (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="text-center py-16"
              >
                <div className="w-20 h-20 mx-auto bg-purple-100 dark:bg-purple-900/30 rounded-2xl flex items-center justify-center mb-6">
                  <Headphones className="w-10 h-10 text-purple-600 dark:text-purple-400" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                  No support metrics available
                </h3>
                <p className="text-gray-600 dark:text-gray-400 max-w-sm mx-auto leading-relaxed">
                  Support metrics will appear here once data is available for the selected time
                  period.
                </p>
              </motion.div>
            )}
          </Tabs.Content>

          <Tabs.Content value="tech-team" className="focus:outline-none">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="space-y-6"
            >
              {/* Tracking Note */}
              <div className="flex items-center gap-2 px-4 py-2 bg-amber-50/80 dark:bg-amber-900/10 border border-amber-200/60 dark:border-amber-700/30 rounded-lg">
                <div className="flex-shrink-0 w-1.5 h-1.5 rounded-full bg-amber-400" />
                <p className="text-xs text-amber-800 dark:text-amber-300">
                  Tech team metrics are tracked starting from{' '}
                  <span className="font-semibold">March 3, 2025</span>. Data prior to this date is
                  not available.
                </p>
              </div>

              {/* Case Volume */}
              <motion.div
                className="relative bg-gradient-to-br from-white via-rose-50/30 to-orange-50/30 dark:from-gray-800/50 dark:via-rose-900/20 dark:to-orange-900/20 rounded-2xl p-5 shadow-lg border border-white/20 dark:border-gray-700/50 backdrop-blur-sm"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.15, type: 'spring', stiffness: 100 }}
              >
                <div className="mb-4">
                  <h3 className="text-lg font-bold bg-gradient-to-r from-rose-600 via-red-600 to-orange-600 bg-clip-text text-transparent mb-1 flex items-center gap-2">
                    <TicketIcon className="w-5 h-5 text-rose-600" />
                    Case Volume
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400 text-xs">
                    Tech team case counts and dev issue tracking
                  </p>
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                  {techTeamMetrics
                    .filter(
                      metric =>
                        metric.id === TECH_TEAM_METRICS.TECH_TEAM_CASES ||
                        metric.id === TECH_TEAM_METRICS.ISSUES_SUBMITTED_DEV ||
                        metric.id === TECH_TEAM_METRICS.ISSUES_RESOLVED_DEV
                    )
                    .map((metric, index) => (
                      <motion.div
                        key={metric.id}
                        initial={{ opacity: 0, y: 15 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2 + index * 0.05 }}
                      >
                        <MetricCard metric={metric} />
                      </motion.div>
                    ))}
                </div>
              </motion.div>

              {/* Queue & Assignment Performance */}
              <motion.div
                className="relative bg-gradient-to-br from-white via-amber-50/30 to-yellow-50/30 dark:from-gray-800/50 dark:via-amber-900/20 dark:to-yellow-900/20 rounded-2xl p-5 shadow-lg border border-white/20 dark:border-gray-700/50 backdrop-blur-sm"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.25, type: 'spring', stiffness: 100 }}
              >
                <div className="mb-4">
                  <h3 className="text-lg font-bold bg-gradient-to-r from-amber-600 via-orange-600 to-red-600 bg-clip-text text-transparent mb-1 flex items-center gap-2">
                    <Timer className="w-5 h-5 text-amber-600" />
                    Queue & Assignment Performance
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400 text-xs">
                    Time from queue assignment to associate pickup
                  </p>
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {techTeamMetrics
                    .filter(
                      metric =>
                        metric.id === TECH_TEAM_METRICS.AVG_ASSIGNMENT_TIME ||
                        metric.id === TECH_TEAM_METRICS.AVG_ASSIGNMENT_TIME_BH
                    )
                    .map((metric, index) => (
                      <motion.div
                        key={metric.id}
                        initial={{ opacity: 0, y: 15 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.3 + index * 0.05 }}
                      >
                        <MetricCard metric={metric} />
                      </motion.div>
                    ))}
                </div>
              </motion.div>

              {/* Response Performance */}
              <motion.div
                className="relative bg-gradient-to-br from-white via-blue-50/30 to-indigo-50/30 dark:from-gray-800/50 dark:via-blue-900/20 dark:to-indigo-900/20 rounded-2xl p-5 shadow-lg border border-white/20 dark:border-gray-700/50 backdrop-blur-sm"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.35, type: 'spring', stiffness: 100 }}
              >
                <div className="mb-4">
                  <h3 className="text-lg font-bold bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 bg-clip-text text-transparent mb-1 flex items-center gap-2">
                    <RocketLaunchIcon className="w-5 h-5 text-blue-600" />
                    Response Performance
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400 text-xs">
                    Time from associate assignment to first outbound response
                  </p>
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {techTeamMetrics
                    .filter(
                      metric =>
                        metric.id === TECH_TEAM_METRICS.AVG_RESPONSE_TIME ||
                        metric.id === TECH_TEAM_METRICS.AVG_RESPONSE_TIME_BH
                    )
                    .map((metric, index) => (
                      <motion.div
                        key={metric.id}
                        initial={{ opacity: 0, y: 15 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.4 + index * 0.05 }}
                      >
                        <MetricCard metric={metric} />
                      </motion.div>
                    ))}
                </div>
              </motion.div>

              {/* Dev Submission & Resolution */}
              <motion.div
                className="relative bg-gradient-to-br from-white via-emerald-50/30 to-teal-50/30 dark:from-gray-800/50 dark:via-emerald-900/20 dark:to-teal-900/20 rounded-2xl p-5 shadow-lg border border-white/20 dark:border-gray-700/50 backdrop-blur-sm"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.45, type: 'spring', stiffness: 100 }}
              >
                <div className="mb-4">
                  <h3 className="text-lg font-bold bg-gradient-to-r from-emerald-600 via-teal-600 to-cyan-600 bg-clip-text text-transparent mb-1 flex items-center gap-2">
                    <CodeBracketIcon className="w-5 h-5 text-emerald-600" />
                    Dev Submission & Resolution
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400 text-xs">
                    Time to submit issues to dev and dev resolution turnaround
                  </p>
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {techTeamMetrics
                    .filter(
                      metric =>
                        metric.id === TECH_TEAM_METRICS.AVG_DEV_SUBMISSION_TIME ||
                        metric.id === TECH_TEAM_METRICS.AVG_DEV_SUBMISSION_TIME_BH ||
                        metric.id === TECH_TEAM_METRICS.AVG_DEV_RESOLUTION_TIME ||
                        metric.id === TECH_TEAM_METRICS.AVG_DEV_RESOLUTION_TIME_BH
                    )
                    .map((metric, index) => (
                      <motion.div
                        key={metric.id}
                        initial={{ opacity: 0, y: 15 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.5 + index * 0.05 }}
                      >
                        <MetricCard metric={metric} />
                      </motion.div>
                    ))}
                </div>
              </motion.div>

              {/* Follow Up Performance */}
              <motion.div
                className="relative bg-gradient-to-br from-white via-purple-50/30 to-fuchsia-50/30 dark:from-gray-800/50 dark:via-purple-900/20 dark:to-fuchsia-900/20 rounded-2xl p-5 shadow-lg border border-white/20 dark:border-gray-700/50 backdrop-blur-sm"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.55, type: 'spring', stiffness: 100 }}
              >
                <div className="mb-4">
                  <h3 className="text-lg font-bold bg-gradient-to-r from-purple-600 via-fuchsia-600 to-pink-600 bg-clip-text text-transparent mb-1 flex items-center gap-2">
                    <CheckCircleIcon className="w-5 h-5 text-purple-600" />
                    Follow Up Performance
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400 text-xs">
                    Time from dev completion to sending follow up to the user
                  </p>
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {techTeamMetrics
                    .filter(
                      metric =>
                        metric.id === TECH_TEAM_METRICS.AVG_DEV_FOLLOWUP_TIME ||
                        metric.id === TECH_TEAM_METRICS.AVG_DEV_FOLLOWUP_TIME_BH
                    )
                    .map((metric, index) => (
                      <motion.div
                        key={metric.id}
                        initial={{ opacity: 0, y: 15 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.6 + index * 0.05 }}
                      >
                        <MetricCard metric={metric} />
                      </motion.div>
                    ))}
                </div>
              </motion.div>

              {techTeamMetrics.length === 0 && !isLoading && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="text-center py-16"
                >
                  <div className="w-20 h-20 mx-auto bg-rose-100 dark:bg-rose-900/30 rounded-2xl flex items-center justify-center mb-6">
                    <CodeBracketIcon className="w-10 h-10 text-rose-600 dark:text-rose-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                    No tech team metrics available
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400 max-w-sm mx-auto leading-relaxed">
                    Tech team metrics will appear here once data is available for the selected time
                    period.
                  </p>
                </motion.div>
              )}
            </motion.div>
          </Tabs.Content>

          <Tabs.Content value="csat" className="focus:outline-none">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
              className="space-y-8"
            >
              <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-6 shadow-xl">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      CSAT Overview
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Customer satisfaction snapshot from submitted surveys
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {(() => {
                    const useDerived = !selectedOwners.includes('all');
                    const metricsToRender = useDerived
                      ? ([
                          {
                            id: 'csat-submissions-filtered',
                            name: 'CSAT Submissions',
                            description:
                              'Cases with a CSAT submitted in the selected range (filtered by support agent)',
                            value: derivedCsatMetrics.submissions,
                            displayType: 'number',
                            category: 'support',
                            color: '#0EA5E9',
                            icon: 'SparklesIcon',
                          },
                          {
                            id: 'csat-avg-score-filtered',
                            name: 'Average CSAT Score',
                            description:
                              'Average CSAT score for the selected agents and date range',
                            value: derivedCsatMetrics.avgScore,
                            displayType: 'score',
                            category: 'support',
                            color: '#6366F1',
                            icon: 'StarIcon',
                          },
                          {
                            id: 'csat-comments-filtered',
                            name: 'CSAT Comments',
                            description: 'CSAT comments for the selected agents and date range',
                            value: derivedCsatMetrics.comments,
                            displayType: 'number',
                            category: 'support',
                            color: '#0EA5E9',
                            icon: 'ChatBubbleLeftRightIcon',
                          },
                        ] as any)
                      : [
                          allMetrics.find(metric => metric.id === SUPPORT_METRICS.CSAT_SUBMISSIONS),
                          allMetrics.find(metric => metric.id === SUPPORT_METRICS.CSAT_AVG_SCORE),
                          allMetrics.find(metric => metric.id === SUPPORT_METRICS.CSAT_COMMENTS),
                        ].filter(Boolean);

                    if (metricsToRender.length === 0) {
                      return (
                        <div className="col-span-full text-center py-10 text-gray-600 dark:text-gray-400">
                          No CSAT data available for the selected range.
                        </div>
                      );
                    }

                    return metricsToRender.map((metric: any) => (
                      <MetricCard key={metric.id} metric={metric} />
                    ));
                  })()}
                </div>

                {/* CSAT details table */}
                <div className="mt-8">
                  <div className="flex items-center justify-between mb-3 flex-wrap gap-3">
                    <div>
                      <h4 className="text-md font-semibold text-gray-900 dark:text-white">
                        CSAT Submission Details
                      </h4>
                    </div>
                    <div className="flex items-center gap-3 text-sm text-gray-600 dark:text-gray-300 flex-wrap">
                      <div className="relative">
                        <button
                          onClick={() => setIsDateSortDropdownOpen(o => !o)}
                          className="px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm hover:shadow-lg transition text-sm font-semibold flex items-center gap-2"
                        >
                          <span className="text-gray-800 dark:text-gray-200">Sort by Date</span>
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            {dateSortOrder === 'desc' ? 'Newest First' : 'Oldest First'}
                          </span>
                        </button>
                        {isDateSortDropdownOpen && (
                          <>
                            <div
                              className="fixed inset-0 z-30"
                              onClick={() => setIsDateSortDropdownOpen(false)}
                            />
                            <div className="absolute z-40 mt-2 w-56 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl shadow-2xl p-3 space-y-2">
                              <div className="flex items-center justify-between mb-2">
                                <span className="text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide">
                                  Sort Order
                                </span>
                              </div>
                              <button
                                onClick={() => {
                                  setDateSortOrder('desc');
                                  setIsDateSortDropdownOpen(false);
                                  setCsatPage(0);
                                }}
                                className={`w-full text-left px-3 py-2 rounded-lg text-sm transition ${
                                  dateSortOrder === 'desc'
                                    ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 font-semibold'
                                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                                }`}
                              >
                                Newest First
                              </button>
                              <button
                                onClick={() => {
                                  setDateSortOrder('asc');
                                  setIsDateSortDropdownOpen(false);
                                  setCsatPage(0);
                                }}
                                className={`w-full text-left px-3 py-2 rounded-lg text-sm transition ${
                                  dateSortOrder === 'asc'
                                    ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 font-semibold'
                                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                                }`}
                              >
                                Oldest First
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                      <div className="relative">
                        <button
                          onClick={() => setIsOwnerDropdownOpen(o => !o)}
                          className="px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm hover:shadow-lg transition text-sm font-semibold flex items-center gap-2"
                        >
                          <span className="text-gray-800 dark:text-gray-200">Support Agent</span>
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            {selectedOwners.includes('all')
                              ? 'All'
                              : `${selectedOwners.length} selected`}
                          </span>
                        </button>
                        {isOwnerDropdownOpen && (
                          <>
                            <div
                              className="fixed inset-0 z-30"
                              onClick={() => setIsOwnerDropdownOpen(false)}
                            />
                            <div className="absolute z-40 mt-2 w-72 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl shadow-2xl p-3 space-y-2 max-h-80 overflow-auto">
                              <div className="flex items-center justify-between">
                                <span className="text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide">
                                  Filter by Support Agent
                                </span>
                                <button
                                  className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
                                  onClick={() => {
                                    setSelectedOwners(['all']);
                                    setCsatPage(0);
                                  }}
                                >
                                  Reset
                                </button>
                              </div>
                              <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200">
                                <input
                                  type="checkbox"
                                  checked={selectedOwners.includes('all')}
                                  onChange={() => {
                                    setSelectedOwners(['all']);
                                    setCsatPage(0);
                                  }}
                                />
                                <span>All</span>
                              </label>
                              <div className="border-t border-gray-200 dark:border-gray-700 pt-2 space-y-2">
                                {csatOwners.map(owner => {
                                  const checked = selectedOwners.includes(owner);

                                  return (
                                    <label
                                      key={owner}
                                      className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200"
                                    >
                                      <input
                                        type="checkbox"
                                        checked={checked}
                                        onChange={() => {
                                          setSelectedOwners(prev => {
                                            if (prev.includes('all')) {
                                              // replace 'all' with this owner
                                              return [owner];
                                            }

                                            if (checked) {
                                              const next = prev.filter(v => v !== owner);

                                              return next.length ? next : ['all'];
                                            }

                                            return [...prev, owner];
                                          });
                                          setCsatPage(0);
                                        }}
                                      />
                                      <span className="truncate">{owner}</span>
                                    </label>
                                  );
                                })}
                                {csatOwners.length === 0 && (
                                  <div className="text-xs text-gray-500 dark:text-gray-400">
                                    No agents in range
                                  </div>
                                )}
                              </div>
                            </div>
                          </>
                        )}
                      </div>
                      <button
                        className="px-3 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm hover:shadow-md transition disabled:opacity-50"
                        onClick={() => setCsatPage(p => Math.max(0, p - 1))}
                        disabled={csatPage === 0}
                      >
                        Prev
                      </button>
                      <span>
                        Page {csatPage + 1} of{' '}
                        {Math.max(1, Math.ceil(filteredCsatRecords.length / CSAT_PAGE_SIZE))}
                      </span>
                      <button
                        className="px-3 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm hover:shadow-md transition disabled:opacity-50"
                        onClick={() =>
                          setCsatPage(p =>
                            p + 1 < Math.ceil(filteredCsatRecords.length / CSAT_PAGE_SIZE)
                              ? p + 1
                              : p
                          )
                        }
                        disabled={
                          csatPage + 1 >=
                          Math.max(1, Math.ceil(filteredCsatRecords.length / CSAT_PAGE_SIZE))
                        }
                      >
                        Next
                      </button>
                    </div>
                  </div>

                  <div className="overflow-x-auto rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-2xl">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 table-fixed">
                      <thead className="bg-gradient-to-r from-gray-50 via-white to-gray-50 dark:from-gray-800 dark:via-gray-800 dark:to-gray-700 sticky top-0 z-10">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wide w-28 border-r border-gray-300 dark:border-gray-600 align-middle">
                            Case #
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wide w-52 border-r border-gray-300 dark:border-gray-600 align-middle">
                            Account
                          </th>
                          <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wide w-40 border-r border-gray-300 dark:border-gray-600 align-middle">
                            Owner
                          </th>
                          <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wide w-24 border-r border-gray-300 dark:border-gray-600 align-middle">
                            CSAT
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wide w-80 border-r border-gray-300 dark:border-gray-600 align-middle">
                            Comment
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wide w-52 align-middle">
                            Submitted
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200 dark:divide-gray-700 text-sm">
                        {filteredCsatRecords
                          .slice(
                            csatPage * CSAT_PAGE_SIZE,
                            csatPage * CSAT_PAGE_SIZE + CSAT_PAGE_SIZE
                          )
                          .map((record, idx) => {
                            const rowKey = `${record.Id || record.CaseNumber || idx}`;
                            const isCaseExpanded = !!expandedCaseRows[rowKey];
                            const isAccountExpanded = !!expandedAccountRows[rowKey];
                            const isOwnerExpanded = !!expandedOwnerRows[rowKey];
                            const isCommentExpanded = !!expandedCommentRows[rowKey];
                            const caseNumber = record.CaseNumber || '—';
                            const ownerName = record.Owner?.Name || '—';
                            const comment =
                              typeof record.CSAT_Comment__c === 'string'
                                ? record.CSAT_Comment__c.trim()
                                : '';
                            const submitted =
                              record.CSAT_Submitted_Date__c &&
                              new Date(record.CSAT_Submitted_Date__c).toLocaleString();

                            return (
                              <tr
                                key={`${record.Id || record.CaseNumber || idx}`}
                                className="hover:bg-blue-50/50 dark:hover:bg-blue-900/20 transition-colors"
                              >
                                <td className="px-4 py-3 text-gray-900 dark:text-gray-100 font-semibold max-w-0 border-r border-gray-200 dark:border-gray-700 align-middle">
                                  <div className="w-full">
                                    <div
                                      className={`${
                                        isCaseExpanded
                                          ? 'whitespace-pre-wrap break-words'
                                          : 'overflow-hidden text-ellipsis whitespace-nowrap'
                                      }`}
                                      title={!isCaseExpanded ? caseNumber : undefined}
                                    >
                                      {caseNumber}
                                    </div>
                                    {caseNumber && caseNumber.length > 10 && (
                                      <button
                                        className="text-xs text-blue-600 dark:text-blue-400 hover:underline mt-1 block"
                                        onClick={() =>
                                          setExpandedCaseRows(prev => ({
                                            ...prev,
                                            [rowKey]: !isCaseExpanded,
                                          }))
                                        }
                                      >
                                        {isCaseExpanded ? 'Collapse' : 'Expand'}
                                      </button>
                                    )}
                                  </div>
                                </td>
                                <td className="px-4 py-3 text-gray-900 dark:text-gray-100 max-w-0 border-r border-gray-200 dark:border-gray-700 align-middle">
                                  <div className="w-full">
                                    <div
                                      className={`${
                                        isAccountExpanded
                                          ? 'whitespace-pre-wrap break-words'
                                          : 'overflow-hidden text-ellipsis whitespace-nowrap'
                                      }`}
                                      title={
                                        !isAccountExpanded
                                          ? record.Account_Name__c || undefined
                                          : undefined
                                      }
                                    >
                                      {record.Account_Name__c || '—'}
                                    </div>
                                    {record.Account_Name__c &&
                                      record.Account_Name__c.length > 30 && (
                                        <button
                                          className="text-xs text-blue-600 dark:text-blue-400 hover:underline mt-1 block"
                                          onClick={() =>
                                            setExpandedAccountRows(prev => ({
                                              ...prev,
                                              [rowKey]: !isAccountExpanded,
                                            }))
                                          }
                                        >
                                          {isAccountExpanded ? 'Collapse' : 'Expand'}
                                        </button>
                                      )}
                                  </div>
                                </td>
                                <td className="px-4 py-3 text-gray-900 dark:text-gray-100 max-w-0 border-r border-gray-200 dark:border-gray-700 align-middle text-center">
                                  <div className="w-full">
                                    <div
                                      className={`${
                                        isOwnerExpanded
                                          ? 'whitespace-pre-wrap break-words'
                                          : 'overflow-hidden text-ellipsis whitespace-nowrap'
                                      }`}
                                      title={!isOwnerExpanded ? ownerName : undefined}
                                    >
                                      {ownerName}
                                    </div>
                                    {ownerName && ownerName.length > 20 && (
                                      <button
                                        className="text-xs text-blue-600 dark:text-blue-400 hover:underline mt-1 block mx-auto"
                                        onClick={() =>
                                          setExpandedOwnerRows(prev => ({
                                            ...prev,
                                            [rowKey]: !isOwnerExpanded,
                                          }))
                                        }
                                      >
                                        {isOwnerExpanded ? 'Collapse' : 'Expand'}
                                      </button>
                                    )}
                                  </div>
                                </td>
                                <td className="px-4 py-3 text-gray-900 dark:text-gray-100 font-semibold border-r border-gray-200 dark:border-gray-700 align-middle text-center">
                                  {record.CSAT__c !== undefined && record.CSAT__c !== null
                                    ? Number(record.CSAT__c).toFixed(1)
                                    : '—'}
                                </td>
                                <td className="px-4 py-3 text-gray-900 dark:text-gray-100 max-w-0 border-r border-gray-200 dark:border-gray-700 align-middle">
                                  <div className="w-full">
                                    <div
                                      className={`${
                                        isCommentExpanded
                                          ? 'whitespace-pre-wrap break-words'
                                          : 'overflow-hidden text-ellipsis whitespace-nowrap'
                                      }`}
                                      title={!isCommentExpanded ? comment : undefined}
                                    >
                                      {comment || '—'}
                                    </div>
                                    {comment && comment.length > 40 && (
                                      <button
                                        className="text-xs text-blue-600 dark:text-blue-400 hover:underline mt-1 block"
                                        onClick={() =>
                                          setExpandedCommentRows(prev => ({
                                            ...prev,
                                            [rowKey]: !isCommentExpanded,
                                          }))
                                        }
                                      >
                                        {isCommentExpanded ? 'Collapse' : 'Expand'}
                                      </button>
                                    )}
                                  </div>
                                </td>
                                <td className="px-4 py-3 text-gray-900 dark:text-gray-100 align-middle">
                                  {submitted || '—'}
                                </td>
                              </tr>
                            );
                          })}
                        {filteredCsatRecords.length === 0 && (
                          <tr>
                            <td
                              colSpan={6}
                              className="px-3 py-6 text-center text-gray-600 dark:text-gray-400"
                            >
                              No CSAT submissions found for the selected range.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </motion.div>
          </Tabs.Content>

          <Tabs.Content value="classifications" className="focus:outline-none">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
              className="space-y-8 max-w-7xl mx-auto"
            >
              <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-6 shadow-xl">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-3">
                      <span>Classifications</span>
                      <span className="text-xs px-2 py-1 rounded-full bg-fuchsia-100 text-fuchsia-700 dark:bg-fuchsia-900/30 dark:text-fuchsia-300 font-semibold">
                        Agentforce Classifications
                      </span>
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Agentforce-powered case classifications (feature area + sentiment).
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-10">
                  {/* Feature Area Classification (AI) */}
                  {(() => {
                    const featureAreaMetric = allMetrics.find(
                      m => m.id === SUPPORT_METRICS.FEATURE_AREA_CLASSIFICATION_BREAKDOWN
                    );

                    if (
                      featureAreaMetric &&
                      featureAreaMetric.value &&
                      typeof featureAreaMetric.value === 'object'
                    ) {
                      const featureAreaData = featureAreaMetric.value as { [key: string]: number };

                      return (
                        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-6 shadow-xl hover:shadow-2xl transition-all duration-300">
                          <CategoryDonutChart
                            data={featureAreaData}
                            title="AI Feature Area Classification"
                            emptyDescription="No feature area classifications found for the selected date range"
                          />
                        </div>
                      );
                    }

                    return (
                      <div className="min-h-[520px] rounded-2xl border border-dashed border-gray-200 dark:border-gray-700 bg-gradient-to-br from-gray-50/40 to-white/60 dark:from-gray-900/30 dark:to-gray-800/30 flex items-center justify-center">
                        <div className="text-center text-gray-600 dark:text-gray-400">
                          No feature area classification data available.
                        </div>
                      </div>
                    );
                  })()}

                  {/* Sentiment Classification (AI) */}
                  {(() => {
                    const sentimentMetric = allMetrics.find(
                      m => m.id === SUPPORT_METRICS.SENTIMENT_CLASSIFICATION_BREAKDOWN
                    );

                    if (
                      sentimentMetric &&
                      sentimentMetric.value &&
                      typeof sentimentMetric.value === 'object'
                    ) {
                      const sentimentData = sentimentMetric.value as { [key: string]: number };

                      return (
                        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-6 shadow-xl hover:shadow-2xl transition-all duration-300">
                          <CaseTypeBarChart
                            data={sentimentData}
                            title="AI Sentiment Classification"
                            emptyDescription="No sentiment classifications found for the selected date range"
                          />
                        </div>
                      );
                    }

                    return (
                      <div className="min-h-[520px] rounded-2xl border border-dashed border-gray-200 dark:border-gray-700 bg-gradient-to-br from-gray-50/40 to-white/60 dark:from-gray-900/30 dark:to-gray-800/30 flex items-center justify-center">
                        <div className="text-center text-gray-600 dark:text-gray-400">
                          No sentiment classification data available.
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </div>
            </motion.div>
          </Tabs.Content>

          <Tabs.Content value="visualizations" className="focus:outline-none">
            {/* Case Origin Donut Chart */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="space-y-8 max-w-7xl mx-auto"
            >
              {/* First row - Two donut charts side by side */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Case Origin Breakdown Chart */}
                {(() => {
                  const caseOriginMetric = allMetrics.find(
                    m => m.id === SUPPORT_METRICS.CASE_ORIGIN_BREAKDOWN
                  );

                  if (
                    caseOriginMetric &&
                    caseOriginMetric.value &&
                    typeof caseOriginMetric.value === 'object'
                  ) {
                    const caseOriginData = caseOriginMetric.value as { [key: string]: number };

                    return (
                      <motion.div
                        className="lg:col-span-1"
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 0.3, duration: 0.6 }}
                      >
                        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-6 shadow-xl hover:shadow-2xl transition-all duration-300 h-full">
                          <CaseOriginDonutChart
                            data={caseOriginData}
                            title="Case Origin Distribution"
                          />
                        </div>
                      </motion.div>
                    );
                  }

                  return null;
                })()}

                {/* Case Product Breakdown Chart */}
                {(() => {
                  const caseProductMetric = allMetrics.find(
                    m => m.id === SUPPORT_METRICS.CASE_PRODUCT_BREAKDOWN
                  );

                  if (
                    caseProductMetric &&
                    caseProductMetric.value &&
                    typeof caseProductMetric.value === 'object'
                  ) {
                    const caseProductData = caseProductMetric.value as { [key: string]: number };

                    return (
                      <motion.div
                        className="lg:col-span-1"
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 0.4, duration: 0.6 }}
                      >
                        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-6 shadow-xl hover:shadow-2xl transition-all duration-300 h-full">
                          <CaseProductDonutChart
                            data={caseProductData}
                            title="Product Distribution"
                          />
                        </div>
                      </motion.div>
                    );
                  }

                  return (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: 0.4, duration: 0.6 }}
                    >
                      <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-6 shadow-xl hover:shadow-2xl transition-all duration-300 h-full flex items-center justify-center min-h-[400px]">
                        <div className="text-center">
                          <div className="w-16 h-16 mx-auto bg-gradient-to-br from-green-100 to-emerald-100 dark:from-green-900/30 dark:to-emerald-900/30 rounded-2xl flex items-center justify-center mb-4">
                            <BarChart3 className="w-8 h-8 text-green-600 dark:text-green-400" />
                          </div>
                          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                            Product Distribution
                          </h3>
                          <p className="text-gray-600 dark:text-gray-400 text-sm">
                            No product data available
                          </p>
                        </div>
                      </div>
                    </motion.div>
                  );
                })()}
              </div>

              {/* Second row - Case Type Bar Chart (full width) */}
              {(() => {
                const caseTypeMetric = allMetrics.find(
                  m => m.id === SUPPORT_METRICS.CASE_TYPE_BREAKDOWN
                );

                if (
                  caseTypeMetric &&
                  caseTypeMetric.value &&
                  typeof caseTypeMetric.value === 'object'
                ) {
                  const caseTypeData = caseTypeMetric.value as { [key: string]: number };

                  return (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: 0.5, duration: 0.6 }}
                    >
                      <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-6 shadow-xl hover:shadow-2xl transition-all duration-300 h-full">
                        <CaseTypeBarChart data={caseTypeData} title="Case Type Breakdown" />
                      </div>
                    </motion.div>
                  );
                }

                return null;
              })()}

              {/* New Chart - Case Type Breakdown by Rep (full width) */}
              {(() => {
                const caseTypeByRepMetric = allMetrics.find(
                  m => m.id === SUPPORT_METRICS.CASE_TYPE_BY_REP_BREAKDOWN
                );

                if (
                  caseTypeByRepMetric &&
                  caseTypeByRepMetric.value &&
                  Array.isArray(caseTypeByRepMetric.value)
                ) {
                  const caseTypeByRepData = caseTypeByRepMetric.value as Array<{
                    Name: string; // Salesforce returns Owner.Name as 'Name'
                    Type: string;
                    total_count: number;
                  }>;

                  return (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: 0.6, duration: 0.6 }}
                    >
                      <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-6 shadow-xl hover:shadow-2xl transition-all duration-300 h-full">
                        <CaseTypeByRepChart
                          data={caseTypeByRepData}
                          title="Case Type Distribution by Agent"
                        />
                      </div>
                    </motion.div>
                  );
                }

                return null;
              })()}

              {/* Third row - Time Distribution Chart (full width) */}
              {(() => {
                const responseTimeMetric = allMetrics.find(
                  m => m.id === SUPPORT_METRICS.FIRST_RESPONSE_TIME_DISTRIBUTION
                );
                const resolutionTimeMetric = allMetrics.find(
                  m => m.id === SUPPORT_METRICS.RESOLUTION_TIME_DISTRIBUTION
                );

                // Show the chart if we have either response time or resolution time data
                if (
                  (responseTimeMetric &&
                    responseTimeMetric.value &&
                    typeof responseTimeMetric.value === 'object' &&
                    Object.keys(responseTimeMetric.value).length > 0) ||
                  (resolutionTimeMetric &&
                    resolutionTimeMetric.value &&
                    typeof resolutionTimeMetric.value === 'object' &&
                    Object.keys(resolutionTimeMetric.value).length > 0)
                ) {
                  // The backend now returns processed bucket data, not raw records
                  const responseTimeData =
                    (responseTimeMetric?.value as TimeDistributionData) || {};
                  const resolutionTimeData =
                    (resolutionTimeMetric?.value as TimeDistributionData) || {};

                  // Get total counts from existing metrics for accurate totals
                  const totalResponseCases = getMetricValue(SUPPORT_METRICS.TOTAL_TICKETS);
                  const totalResolutionCases = getMetricValue(SUPPORT_METRICS.TOTAL_TICKETS); // Same as total tickets since resolution is a subset

                  return (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: 0.6, duration: 0.6 }}
                      className="mt-8"
                    >
                      <TimeDistributionChart
                        responseTimeData={responseTimeData}
                        resolutionTimeData={resolutionTimeData}
                        totalResponseCases={totalResponseCases}
                        totalResolutionCases={totalResolutionCases}
                        isLoading={isLoading}
                      />
                    </motion.div>
                  );
                }

                return null;
              })()}
            </motion.div>

            {/* Empty State if no Case Origin data */}
            {(() => {
              const caseOriginMetric = allMetrics.find(
                m => m.id === SUPPORT_METRICS.CASE_ORIGIN_BREAKDOWN
              );

              if (
                !caseOriginMetric ||
                !caseOriginMetric.value ||
                typeof caseOriginMetric.value !== 'object'
              ) {
                return (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="text-center py-16"
                  >
                    <div className="w-20 h-20 mx-auto bg-purple-100 dark:bg-purple-900/30 rounded-2xl flex items-center justify-center mb-6">
                      <PieChart className="w-10 h-10 text-purple-600 dark:text-purple-400" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                      No visualization data available
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400 max-w-sm mx-auto leading-relaxed">
                      Visualizations will appear here once case origin data is available for the
                      selected time period.
                    </p>
                  </motion.div>
                );
              }

              return null;
            })()}
          </Tabs.Content>

          {/* Leaderboard Tab Content */}
          <Tabs.Content value="leaderboard" className="focus:outline-none">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="max-w-7xl mx-auto"
            >
              {(() => {
                // Function to get appropriate icon for each metric
                const getMetricIcon = (metricId: string) => {
                  if (metricId.includes('total-tickets') || metricId.includes('all-time-tickets')) {
                    return <TicketIcon className="w-4 h-4" />;
                  }

                  if (metricId.includes('csat')) {
                    return <StarIcon className="w-4 h-4" />;
                  }

                  if (metricId.includes('response-time')) {
                    return <Timer className="w-4 h-4" />;
                  }

                  if (metricId.includes('resolution-time')) {
                    return <CheckCircleIcon className="w-4 h-4" />;
                  }

                  if (metricId.includes('escalated') || metricId.includes('dev')) {
                    return <CodeBracketIcon className="w-4 h-4" />;
                  }

                  if (metricId.includes('escalation-rate')) {
                    return <Percent className="w-4 h-4" />;
                  }

                  if (metricId.includes('first-touch')) {
                    return <Zap className="w-4 h-4" />;
                  }

                  if (metricId.includes('resolution-rate')) {
                    return <Target className="w-4 h-4" />;
                  }

                  return <ChartBarIcon className="w-4 h-4" />;
                };

                // Get all leaderboard metrics and reorder them for better grouping
                const rawLeaderboardMetrics = allMetrics.filter(
                  metric => metric.displayType === ('leaderboard' as any)
                );

                // Define desired order: group related metrics together
                const metricOrder = [
                  'support-total-tickets-leaderboard',
                  'support-all-time-tickets-leaderboard',
                  'support-avg-first-response-time-leaderboard',
                  'support-avg-first-response-time-bh-leaderboard',
                  'support-avg-resolution-time-leaderboard',
                  'support-avg-resolution-time-bh-leaderboard',
                  SUPPORT_METRICS.CSAT_AVG_SCORE_LEADERBOARD,
                  'support-cases-escalated-dev-leaderboard',
                  'support-dev-escalation-rate-leaderboard',
                  'support-first-touch-resolution-leaderboard',
                  'support-first-touch-resolution-rate-leaderboard',
                ];

                // Reorder metrics according to the desired order
                const orderedMetrics: typeof rawLeaderboardMetrics = [];
                const usedMetrics = new Set<string>();

                // Add metrics in the desired order
                metricOrder.forEach(id => {
                  const metric = rawLeaderboardMetrics.find(m => m.id === id);

                  if (metric) {
                    orderedMetrics.push(metric);
                    usedMetrics.add(metric.id);
                  }
                });

                // Add any remaining metrics that weren't in our order
                rawLeaderboardMetrics.forEach(metric => {
                  if (!usedMetrics.has(metric.id)) {
                    orderedMetrics.push(metric);
                  }
                });

                const leaderboardMetrics = orderedMetrics;

                // Get all unique support agents from all leaderboard metrics
                const allSupportAgents = new Set<string>();
                leaderboardMetrics.forEach(metric => {
                  if (metric.value && typeof metric.value === 'object') {
                    Object.keys(metric.value as { [key: string]: number }).forEach(agent => {
                      allSupportAgents.add(agent);
                    });
                  }
                });

                // Sort support agents by Total Support Tickets (primary) then by other metrics
                const sortedSupportAgents = Array.from(allSupportAgents).sort((a, b) => {
                  // Primary sort: Total Support Tickets (date-filtered)
                  const totalTicketsMetric = leaderboardMetrics.find(
                    m => m.id === SUPPORT_METRICS.TOTAL_TICKETS_LEADERBOARD
                  );
                  const totalTicketsA = totalTicketsMetric?.value
                    ? (totalTicketsMetric.value as { [key: string]: number })[a] || 0
                    : 0;
                  const totalTicketsB = totalTicketsMetric?.value
                    ? (totalTicketsMetric.value as { [key: string]: number })[b] || 0
                    : 0;

                  // If Total Support Tickets are different, sort by that
                  if (totalTicketsA !== totalTicketsB) {
                    return totalTicketsB - totalTicketsA; // Descending order
                  }

                  // Secondary sort: Sum of all other metrics (excluding all-time tickets to avoid double counting)
                  const otherMetricsA = leaderboardMetrics
                    .filter(m => m.id !== SUPPORT_METRICS.TOTAL_TICKETS_LEADERBOARD)
                    .reduce((sum, metric) => {
                      const metricValue = metric.value as { [key: string]: number } | undefined;

                      return sum + (metricValue?.[a] || 0);
                    }, 0);
                  const otherMetricsB = leaderboardMetrics
                    .filter(m => m.id !== SUPPORT_METRICS.TOTAL_TICKETS_LEADERBOARD)
                    .reduce((sum, metric) => {
                      const metricValue = metric.value as { [key: string]: number } | undefined;

                      return sum + (metricValue?.[b] || 0);
                    }, 0);

                  return otherMetricsB - otherMetricsA; // Descending order
                });

                if (leaderboardMetrics.length === 0) {
                  return (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      className="text-center py-16"
                    >
                      <div className="w-20 h-20 mx-auto bg-gradient-to-br from-amber-100 to-orange-100 dark:from-amber-900/30 dark:to-orange-900/30 rounded-2xl flex items-center justify-center mb-6">
                        <Trophy className="w-10 h-10 text-amber-600 dark:text-amber-400" />
                      </div>
                      <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                        No Leaderboard Data Available
                      </h3>
                      <p className="text-gray-600 dark:text-gray-400 max-w-md mx-auto leading-relaxed">
                        Leaderboard data will appear here once support agents have case activity for
                        the selected time period.
                      </p>
                    </motion.div>
                  );
                }

                return (
                  <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-xl overflow-hidden">
                    {/* Header - Compact */}
                    <div className="bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600 px-5 py-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <motion.div
                            className="p-2 rounded-lg bg-gradient-to-br from-amber-50 via-yellow-50 to-orange-50 dark:from-amber-900/20 dark:via-yellow-900/20 dark:to-orange-900/20 shadow-md border border-amber-100/50 dark:border-amber-800/30"
                            whileHover={{ scale: 1.05, rotate: 5 }}
                            transition={{ duration: 0.2 }}
                          >
                            <Trophy className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                          </motion.div>
                          <div className="flex flex-col">
                            <h3 className="text-lg font-bold bg-gradient-to-r from-gray-800 via-gray-700 to-gray-600 dark:from-white dark:via-gray-100 dark:to-gray-200 bg-clip-text text-transparent">
                              Support Team Leaderboard
                            </h3>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                              Performance metrics and rankings
                            </p>
                          </div>
                        </div>

                        {/* Scroll Hint Arrow */}
                        {leaderboardMetrics.length > 0 && (
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-gray-500 dark:text-gray-400 font-medium">
                              Scroll for more metrics
                            </span>
                            <div className="bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full p-1.5 shadow-md">
                              <svg
                                className="w-3 h-3 text-white"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2.5}
                                  d="M9 5l7 7-7 7"
                                />
                              </svg>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Monday.com Style Table Container */}
                    <div className="relative">
                      {/* Fixed Columns Container (Rank + Agent) */}
                      <div className="absolute left-0 top-0 z-20 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700">
                        {/* Fixed Header - Compact */}
                        <div className="bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
                          <div className="flex h-20">
                            <motion.div
                              className="w-24 px-3 py-3 border-r border-gray-300 dark:border-gray-500 flex flex-col items-center justify-center gap-1 bg-gradient-to-b from-gray-50 to-gray-100 dark:from-gray-700 dark:to-gray-800 hover:from-blue-50 hover:to-blue-100 dark:hover:from-blue-900/20 dark:hover:to-blue-800/20 transition-all duration-200"
                              transition={{ duration: 0.2 }}
                            >
                              <motion.div
                                className="p-1 rounded-md bg-white dark:bg-gray-700 shadow-sm border border-gray-200 dark:border-gray-600"
                                whileHover={{ rotate: 5, scale: 1.05 }}
                                transition={{ duration: 0.15 }}
                              >
                                <div className="text-blue-600 dark:text-blue-400">
                                  <Trophy className="w-3.5 h-3.5" />
                                </div>
                              </motion.div>
                              <div className="text-center text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wide leading-tight">
                                Rank
                              </div>
                            </motion.div>
                            <motion.div
                              className="w-52 px-3 py-3 text-center text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wide flex flex-col items-center justify-center gap-1.5 bg-gradient-to-b from-gray-50 to-gray-100 dark:from-gray-700 dark:to-gray-800 hover:from-blue-50 hover:to-blue-100 dark:hover:from-blue-900/20 dark:hover:to-blue-800/20 transition-all duration-200"
                              transition={{ duration: 0.2 }}
                            >
                              <motion.div
                                className="p-1 rounded-md bg-white dark:bg-gray-700 shadow-sm border border-gray-200 dark:border-gray-600"
                                whileHover={{ rotate: 5, scale: 1.05 }}
                                transition={{ duration: 0.15 }}
                              >
                                <div className="text-blue-600 dark:text-blue-400">
                                  <Headphones className="w-3.5 h-3.5" />
                                </div>
                              </motion.div>
                              <div className="truncate text-center leading-tight">
                                Support Agent
                              </div>
                            </motion.div>
                          </div>
                        </div>

                        {/* Fixed Rows */}
                        <div className="divide-y divide-gray-200 dark:divide-gray-700">
                          {sortedSupportAgents.map((agent, index) => {
                            // Get color scheme based on rank - 15 unique color combinations
                            const getRowColorScheme = (idx: number) => {
                              const schemes = [
                                {
                                  light:
                                    'bg-gradient-to-r from-yellow-50/80 via-amber-50/60 to-orange-50/40',
                                  dark: 'dark:bg-gradient-to-r dark:from-yellow-900/20 dark:via-amber-900/15 dark:to-orange-900/10',
                                }, // 1. Gold
                                {
                                  light:
                                    'bg-gradient-to-r from-gray-50/80 via-slate-50/60 to-zinc-50/40',
                                  dark: 'dark:bg-gradient-to-r dark:from-gray-800/20 dark:via-slate-800/15 dark:to-zinc-800/10',
                                }, // 2. Silver
                                {
                                  light:
                                    'bg-gradient-to-r from-amber-50/80 via-orange-50/60 to-yellow-50/40',
                                  dark: 'dark:bg-gradient-to-r dark:from-amber-900/20 dark:via-orange-900/15 dark:to-yellow-900/10',
                                }, // 3. Bronze
                                {
                                  light:
                                    'bg-gradient-to-r from-blue-50/80 via-indigo-50/60 to-purple-50/40',
                                  dark: 'dark:bg-gradient-to-r dark:from-blue-900/20 dark:via-indigo-900/15 dark:to-purple-900/10',
                                }, // 4. Blue-Purple
                                {
                                  light:
                                    'bg-gradient-to-r from-green-50/80 via-emerald-50/60 to-teal-50/40',
                                  dark: 'dark:bg-gradient-to-r dark:from-green-900/20 dark:via-emerald-900/15 dark:to-teal-900/10',
                                }, // 5. Green-Teal
                                {
                                  light:
                                    'bg-gradient-to-r from-purple-50/80 via-pink-50/60 to-fuchsia-50/40',
                                  dark: 'dark:bg-gradient-to-r dark:from-purple-900/20 dark:via-pink-900/15 dark:to-fuchsia-900/10',
                                }, // 6. Purple-Pink
                                {
                                  light:
                                    'bg-gradient-to-r from-rose-50/80 via-red-50/60 to-orange-50/40',
                                  dark: 'dark:bg-gradient-to-r dark:from-rose-900/20 dark:via-red-900/15 dark:to-orange-900/10',
                                }, // 7. Rose-Red
                                {
                                  light:
                                    'bg-gradient-to-r from-cyan-50/80 via-sky-50/60 to-blue-50/40',
                                  dark: 'dark:bg-gradient-to-r dark:from-cyan-900/20 dark:via-sky-900/15 dark:to-blue-900/10',
                                }, // 8. Cyan-Sky
                                {
                                  light:
                                    'bg-gradient-to-r from-lime-50/80 via-green-50/60 to-emerald-50/40',
                                  dark: 'dark:bg-gradient-to-r dark:from-lime-900/20 dark:via-green-900/15 dark:to-emerald-900/10',
                                }, // 9. Lime-Green
                                {
                                  light:
                                    'bg-gradient-to-r from-violet-50/80 via-purple-50/60 to-indigo-50/40',
                                  dark: 'dark:bg-gradient-to-r dark:from-violet-900/20 dark:via-purple-900/15 dark:to-indigo-900/10',
                                }, // 10. Violet-Indigo
                                {
                                  light:
                                    'bg-gradient-to-r from-fuchsia-50/80 via-pink-50/60 to-rose-50/40',
                                  dark: 'dark:bg-gradient-to-r dark:from-fuchsia-900/20 dark:via-pink-900/15 dark:to-rose-900/10',
                                }, // 11. Fuchsia-Rose
                                {
                                  light:
                                    'bg-gradient-to-r from-teal-50/80 via-cyan-50/60 to-sky-50/40',
                                  dark: 'dark:bg-gradient-to-r dark:from-teal-900/20 dark:via-cyan-900/15 dark:to-sky-900/10',
                                }, // 12. Teal-Cyan
                                {
                                  light:
                                    'bg-gradient-to-r from-orange-50/80 via-amber-50/60 to-yellow-50/40',
                                  dark: 'dark:bg-gradient-to-r dark:from-orange-900/20 dark:via-amber-900/15 dark:to-yellow-900/10',
                                }, // 13. Orange-Amber
                                {
                                  light:
                                    'bg-gradient-to-r from-indigo-50/80 via-blue-50/60 to-cyan-50/40',
                                  dark: 'dark:bg-gradient-to-r dark:from-indigo-900/20 dark:via-blue-900/15 dark:to-cyan-900/10',
                                }, // 14. Indigo-Cyan
                                {
                                  light:
                                    'bg-gradient-to-r from-pink-50/80 via-rose-50/60 to-red-50/40',
                                  dark: 'dark:bg-gradient-to-r dark:from-pink-900/20 dark:via-rose-900/15 dark:to-red-900/10',
                                }, // 15. Pink-Red
                              ];

                              return schemes[idx % schemes.length];
                            };

                            const isHovered = hoveredRowIndex === index;
                            const colorScheme = getRowColorScheme(index);

                            return (
                              <motion.div
                                key={agent}
                                className={`group flex relative border-b border-gray-200 dark:border-gray-700 transition-all duration-300 ${isHovered ? `${colorScheme.light} ${colorScheme.dark}` : ''}`}
                                style={{
                                  boxShadow: isHovered
                                    ? '0 8px 20px rgba(59, 130, 246, 0.12)'
                                    : 'none',
                                }}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{
                                  opacity: 1,
                                  y: 0,
                                }}
                                transition={{
                                  delay: 0.1 + index * 0.05,
                                  type: 'spring',
                                  stiffness: 300,
                                  damping: 25,
                                }}
                                onMouseEnter={() => setHoveredRowIndex(index)}
                                onMouseLeave={() => setHoveredRowIndex(null)}
                              >
                                {/* Rank Column - Compact */}
                                <div className="w-24 px-3 py-3 border-r border-gray-300 dark:border-gray-500 flex items-center justify-center h-14">
                                  {index === 0 && (
                                    <motion.div
                                      className="relative"
                                      whileHover={{ scale: 1.05, rotate: 3 }}
                                      transition={{ type: 'spring', stiffness: 350, damping: 22 }}
                                    >
                                      <div className="w-9 h-9 bg-gradient-to-br from-yellow-300 via-yellow-400 to-yellow-600 rounded-full flex items-center justify-center shadow-md border border-yellow-200">
                                        <Trophy className="w-4 h-4 text-yellow-800" />
                                      </div>
                                      <div className="absolute -top-0.5 -right-0.5 w-3.5 h-3.5 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full flex items-center justify-center text-[10px] font-bold text-white shadow-sm">
                                        1
                                      </div>
                                    </motion.div>
                                  )}
                                  {index === 1 && (
                                    <motion.div
                                      className="relative"
                                      whileHover={{ scale: 1.05, rotate: -2 }}
                                      transition={{ type: 'spring', stiffness: 350, damping: 22 }}
                                    >
                                      <div className="w-9 h-9 bg-gradient-to-br from-gray-300 via-gray-400 to-gray-500 rounded-full flex items-center justify-center shadow-md border border-gray-200">
                                        <Trophy className="w-4 h-4 text-gray-700" />
                                      </div>
                                      <div className="absolute -top-0.5 -right-0.5 w-3.5 h-3.5 bg-gradient-to-br from-gray-400 to-gray-600 rounded-full flex items-center justify-center text-[10px] font-bold text-white shadow-sm">
                                        2
                                      </div>
                                    </motion.div>
                                  )}
                                  {index === 2 && (
                                    <motion.div
                                      className="relative"
                                      whileHover={{ scale: 1.05, rotate: 2 }}
                                      transition={{ type: 'spring', stiffness: 350, damping: 22 }}
                                    >
                                      <div className="w-9 h-9 bg-gradient-to-br from-amber-400 via-amber-500 to-amber-700 rounded-full flex items-center justify-center shadow-md border border-amber-200">
                                        <Trophy className="w-4 h-4 text-amber-800" />
                                      </div>
                                      <div className="absolute -top-0.5 -right-0.5 w-3.5 h-3.5 bg-gradient-to-br from-amber-500 to-amber-700 rounded-full flex items-center justify-center text-[10px] font-bold text-white shadow-sm">
                                        3
                                      </div>
                                    </motion.div>
                                  )}
                                  {index === 3 && (
                                    <motion.div
                                      className="relative"
                                      whileHover={{ scale: 1.03 }}
                                      transition={{ type: 'spring', stiffness: 350, damping: 22 }}
                                    >
                                      <div className="w-8 h-8 bg-gradient-to-br from-blue-400 via-blue-500 to-blue-600 rounded-full flex items-center justify-center shadow-sm border border-blue-200">
                                        <span className="text-white text-xs font-bold">4</span>
                                      </div>
                                    </motion.div>
                                  )}
                                  {index === 4 && (
                                    <motion.div
                                      className="relative"
                                      whileHover={{ scale: 1.03 }}
                                      transition={{ type: 'spring', stiffness: 350, damping: 22 }}
                                    >
                                      <div className="w-8 h-8 bg-gradient-to-br from-green-400 via-green-500 to-green-600 rounded-full flex items-center justify-center shadow-sm border border-green-200">
                                        <span className="text-white text-xs font-bold">5</span>
                                      </div>
                                    </motion.div>
                                  )}
                                  {index > 4 && (
                                    <div className="w-7 h-7 bg-gradient-to-r from-gray-200 to-gray-300 dark:from-gray-600 dark:to-gray-700 rounded-full flex items-center justify-center">
                                      <span className="text-gray-700 dark:text-gray-300 text-xs font-semibold">
                                        {index + 1}
                                      </span>
                                    </div>
                                  )}
                                </div>

                                {/* Agent Name Column - Compact */}
                                <div className="w-52 px-3 py-3 flex items-center justify-center h-14">
                                  <motion.div
                                    className="text-sm font-bold text-slate-800 dark:text-white truncate text-center group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors duration-300"
                                    whileHover={{ scale: 1.02 }}
                                    transition={{ type: 'spring', stiffness: 400, damping: 25 }}
                                  >
                                    {agent}
                                  </motion.div>
                                </div>
                              </motion.div>
                            );
                          })}
                        </div>
                      </div>

                      {/* Scrollable Metrics Container - Compact */}
                      <div className="ml-76 overflow-x-auto" style={{ scrollbarWidth: 'thin' }}>
                        <div className="min-w-max">
                          {/* Scrollable Header - Compact */}
                          <div className="bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
                            <div className="flex h-20">
                              {leaderboardMetrics.map((metric, metricIndex) => (
                                <motion.div
                                  key={metric.id}
                                  className={`w-60 px-3 py-3 text-center text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wide flex flex-col items-center justify-center gap-1.5 bg-gradient-to-b from-gray-50 to-gray-100 dark:from-gray-700 dark:to-gray-800 hover:from-blue-50 hover:to-blue-100 dark:hover:from-blue-900/20 dark:hover:to-blue-800/20 transition-all duration-200 ${
                                    metricIndex < leaderboardMetrics.length - 1
                                      ? 'border-r border-gray-300 dark:border-gray-500'
                                      : ''
                                  }`}
                                  transition={{ duration: 0.2 }}
                                >
                                  <motion.div
                                    className="p-1 rounded-md bg-white dark:bg-gray-700 shadow-sm border border-gray-200 dark:border-gray-600"
                                    whileHover={{ rotate: 5, scale: 1.05 }}
                                    transition={{ duration: 0.15 }}
                                  >
                                    <div className="text-blue-600 dark:text-blue-400">
                                      {getMetricIcon(metric.id)}
                                    </div>
                                  </motion.div>
                                  <div
                                    className="truncate text-center leading-tight"
                                    title={metric.name}
                                  >
                                    {metric.name}
                                  </div>
                                </motion.div>
                              ))}
                            </div>
                          </div>

                          {/* Scrollable Rows */}
                          <div className="divide-y divide-gray-200 dark:divide-gray-700">
                            {sortedSupportAgents.map((agent, index) => {
                              // Get color scheme based on rank - 15 unique color combinations
                              const getRowColorScheme = (idx: number) => {
                                const schemes = [
                                  {
                                    light:
                                      'bg-gradient-to-r from-yellow-50/80 via-amber-50/60 to-orange-50/40',
                                    dark: 'dark:bg-gradient-to-r dark:from-yellow-900/20 dark:via-amber-900/15 dark:to-orange-900/10',
                                  }, // 1. Gold
                                  {
                                    light:
                                      'bg-gradient-to-r from-gray-50/80 via-slate-50/60 to-zinc-50/40',
                                    dark: 'dark:bg-gradient-to-r dark:from-gray-800/20 dark:via-slate-800/15 dark:to-zinc-800/10',
                                  }, // 2. Silver
                                  {
                                    light:
                                      'bg-gradient-to-r from-amber-50/80 via-orange-50/60 to-yellow-50/40',
                                    dark: 'dark:bg-gradient-to-r dark:from-amber-900/20 dark:via-orange-900/15 dark:to-yellow-900/10',
                                  }, // 3. Bronze
                                  {
                                    light:
                                      'bg-gradient-to-r from-blue-50/80 via-indigo-50/60 to-purple-50/40',
                                    dark: 'dark:bg-gradient-to-r dark:from-blue-900/20 dark:via-indigo-900/15 dark:to-purple-900/10',
                                  }, // 4. Blue-Purple
                                  {
                                    light:
                                      'bg-gradient-to-r from-green-50/80 via-emerald-50/60 to-teal-50/40',
                                    dark: 'dark:bg-gradient-to-r dark:from-green-900/20 dark:via-emerald-900/15 dark:to-teal-900/10',
                                  }, // 5. Green-Teal
                                  {
                                    light:
                                      'bg-gradient-to-r from-purple-50/80 via-pink-50/60 to-fuchsia-50/40',
                                    dark: 'dark:bg-gradient-to-r dark:from-purple-900/20 dark:via-pink-900/15 dark:to-fuchsia-900/10',
                                  }, // 6. Purple-Pink
                                  {
                                    light:
                                      'bg-gradient-to-r from-rose-50/80 via-red-50/60 to-orange-50/40',
                                    dark: 'dark:bg-gradient-to-r dark:from-rose-900/20 dark:via-red-900/15 dark:to-orange-900/10',
                                  }, // 7. Rose-Red
                                  {
                                    light:
                                      'bg-gradient-to-r from-cyan-50/80 via-sky-50/60 to-blue-50/40',
                                    dark: 'dark:bg-gradient-to-r dark:from-cyan-900/20 dark:via-sky-900/15 dark:to-blue-900/10',
                                  }, // 8. Cyan-Sky
                                  {
                                    light:
                                      'bg-gradient-to-r from-lime-50/80 via-green-50/60 to-emerald-50/40',
                                    dark: 'dark:bg-gradient-to-r dark:from-lime-900/20 dark:via-green-900/15 dark:to-emerald-900/10',
                                  }, // 9. Lime-Green
                                  {
                                    light:
                                      'bg-gradient-to-r from-violet-50/80 via-purple-50/60 to-indigo-50/40',
                                    dark: 'dark:bg-gradient-to-r dark:from-violet-900/20 dark:via-purple-900/15 dark:to-indigo-900/10',
                                  }, // 10. Violet-Indigo
                                  {
                                    light:
                                      'bg-gradient-to-r from-fuchsia-50/80 via-pink-50/60 to-rose-50/40',
                                    dark: 'dark:bg-gradient-to-r dark:from-fuchsia-900/20 dark:via-pink-900/15 dark:to-rose-900/10',
                                  }, // 11. Fuchsia-Rose
                                  {
                                    light:
                                      'bg-gradient-to-r from-teal-50/80 via-cyan-50/60 to-sky-50/40',
                                    dark: 'dark:bg-gradient-to-r dark:from-teal-900/20 dark:via-cyan-900/15 dark:to-sky-900/10',
                                  }, // 12. Teal-Cyan
                                  {
                                    light:
                                      'bg-gradient-to-r from-orange-50/80 via-amber-50/60 to-yellow-50/40',
                                    dark: 'dark:bg-gradient-to-r dark:from-orange-900/20 dark:via-amber-900/15 dark:to-yellow-900/10',
                                  }, // 13. Orange-Amber
                                  {
                                    light:
                                      'bg-gradient-to-r from-indigo-50/80 via-blue-50/60 to-cyan-50/40',
                                    dark: 'dark:bg-gradient-to-r dark:from-indigo-900/20 dark:via-blue-900/15 dark:to-cyan-900/10',
                                  }, // 14. Indigo-Cyan
                                  {
                                    light:
                                      'bg-gradient-to-r from-pink-50/80 via-rose-50/60 to-red-50/40',
                                    dark: 'dark:bg-gradient-to-r dark:from-pink-900/20 dark:via-rose-900/15 dark:to-red-900/10',
                                  }, // 15. Pink-Red
                                ];

                                return schemes[idx % schemes.length];
                              };

                              const isHovered = hoveredRowIndex === index;
                              const colorScheme = getRowColorScheme(index);

                              return (
                                <motion.div
                                  key={agent}
                                  className={`group flex relative border-b border-gray-200 dark:border-gray-700 transition-all duration-300 ${isHovered ? `${colorScheme.light} ${colorScheme.dark}` : ''}`}
                                  style={{
                                    boxShadow: isHovered
                                      ? '0 8px 20px rgba(59, 130, 246, 0.12)'
                                      : 'none',
                                  }}
                                  initial={{ opacity: 0, y: 20 }}
                                  animate={{
                                    opacity: 1,
                                    y: 0,
                                  }}
                                  transition={{
                                    delay: 0.1 + index * 0.05,
                                    type: 'spring',
                                    stiffness: 300,
                                    damping: 25,
                                  }}
                                  onMouseEnter={() => setHoveredRowIndex(index)}
                                  onMouseLeave={() => setHoveredRowIndex(null)}
                                >
                                  {leaderboardMetrics.map((metric, metricIndex) => {
                                    const metricValue = metric.value as
                                      | { [key: string]: number }
                                      | undefined;
                                    const value = metricValue?.[agent] || 0;

                                    // Format value based on metric type
                                    let formattedValue = value.toLocaleString();

                                    if (
                                      metric.id.includes('response-time') ||
                                      metric.id.includes('resolution-time')
                                    ) {
                                      const hours = value;
                                      const days = Math.floor(hours / 24);
                                      const remainingHours = hours % 24;

                                      if (days > 0) {
                                        // Show days and remaining hours: "25.5h (1d 1.5h)"
                                        formattedValue = `${hours.toFixed(1)}h (${days}d ${remainingHours.toFixed(1)}h)`;
                                      } else {
                                        // Just show hours if less than 24h: "23.5h"
                                        formattedValue = `${hours.toFixed(1)}h`;
                                      }
                                    } else if (
                                      metric.displayType === 'percentage' ||
                                      metric.id.includes('-rate-leaderboard')
                                    ) {
                                      // Format percentage/rate metrics with 1 decimal place
                                      formattedValue = `${value.toFixed(1)}%`;
                                    } else if (
                                      metric.id === SUPPORT_METRICS.CSAT_AVG_SCORE_LEADERBOARD
                                    ) {
                                      formattedValue = value.toFixed(1);
                                    }

                                    return (
                                      <motion.div
                                        key={metric.id}
                                        className={`w-60 px-3 py-3 flex items-center justify-center h-14 relative group/cell ${
                                          metricIndex < leaderboardMetrics.length - 1
                                            ? 'border-r border-gray-300 dark:border-gray-500'
                                            : ''
                                        }`}
                                        initial={{ opacity: 0, scale: 0.95, y: 8 }}
                                        animate={{ opacity: 1, scale: 1, y: 0 }}
                                        transition={{
                                          delay: 0.1 + index * 0.05 + metricIndex * 0.02,
                                          duration: 0.4,
                                          type: 'spring',
                                          stiffness: 350,
                                          damping: 25,
                                        }}
                                      >
                                        <motion.div
                                          className="relative inline-block"
                                          whileHover={{
                                            scale: 1.03,
                                            transition: {
                                              type: 'spring',
                                              stiffness: 400,
                                              damping: 22,
                                            },
                                          }}
                                        >
                                          <span className="text-sm font-bold text-slate-800 dark:text-white group-hover/cell:text-blue-600 dark:group-hover/cell:text-blue-400 transition-colors duration-300">
                                            {formattedValue}
                                          </span>
                                        </motion.div>
                                      </motion.div>
                                    );
                                  })}
                                </motion.div>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })()}
            </motion.div>
          </Tabs.Content>
        </Tabs.Root>
      </div>
    </div>
  );
};

export default SupportDashboard;
